import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import {
  faBan,
  faPencil,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { NotifierService } from "angular-notifier";
import { FormField } from "src/app/interfaces/form-field.model";
import { PriceListTemplate } from "src/app/models/sales/price-list-template";
import { DialogService } from "src/app/services/dialog.service";
import { PriceListTemplateService } from "src/app/services/sales/price-list-template.service";
import {
  getEmptyGuid,
  isEmptyGuid,
} from "src/app/services/util.service";

@Component({
  selector: "app-price-list-template-detail",
  templateUrl: "./price-list-template-detail.component.html",
  styleUrls: ["./price-list-template-detail.component.css"],
})
export class PriceListTemplateDetailComponent implements OnInit {
  faPencil = faPencil;
  faBan = faBan;
  faPlus = faPlus;
  faTrash = faTrash;

  mode = "Add";
  templateId = getEmptyGuid();
  private templateDetails: Partial<PriceListTemplate> = {};

  formFields: FormField[] = [
    { type: "text", id: "MODEL", label: "Model", value: "", required: true },
    { type: "number", id: "ENGINECAP", label: "Eng. Cap", value: null },
    {
      type: "text",
      id: "CATEGORY",
      label: "Category",
      value: "",
      required: true,
      maxLength: 1,
    },
    { type: "number", id: "SEQ", label: "Sequence", value: null, required: true },
    { type: "number", id: "SUBSEQ", label: "Model Sub Seq", value: null, required: true },
    { type: "text", id: "StringDesc", label: "Desc", value: "" },
    {
      type: "switch",
      id: "DisplayPassenger",
      label: "Display in Print Passenger Price List report",
      value: false,
    },
    {
      type: "switch",
      id: "DisplayCommercial",
      label: "Display in Print Commercial Price List report",
      value: false,
    },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private priceListTemplateService: PriceListTemplateService,
    private notifier: NotifierService,
    private dialogService: DialogService,
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe((params) => {
      this.templateId = params["id"];
      if (!isEmptyGuid(this.templateId)) {
        this.mode = "Edit";
        this.loadPriceListTemplateDetail();
      }
    });
  }

  onSave(): void {
    if (!this.isFormValid()) {
      return;
    }
    if (this.mode === "Add") {
      this.addPriceListTemplate();
      return;
    }
    this.updatePriceListTemplate();
  }

  async deleteTemplate(): Promise<void> {
    const confirmed = await this.dialogService.openDeleteDialog(
      "this Price List Template",
    );
    if (!confirmed) {
      return;
    }

    this.priceListTemplateService.deletePriceListTemplate(this.templateId).subscribe({
      next: (response) => {
        if (response.code !== 0) {
          this.notifier.notify("error", response.message);
          return;
        }
        this.notifier.notify("success", "Price List Template deleted successfully");
        this.onCancel();
      },
      error: (error) => {
        console.error("Error deleting Price List Template:", error);
        this.notifier.notify("error", "Error deleting Price List Template");
      },
    });
  }

  isFormValid(): boolean {
    return this.formFields
      .filter((field) => field.required)
      .every(
        (field) =>
          field.value !== null && field.value !== undefined && field.value !== "",
      );
  }

  onCancel(): void {
    this.router.navigate(["../../price-list-template"], {
      relativeTo: this.route,
    });
  }

  private loadPriceListTemplateDetail(): void {
    this.priceListTemplateService
      .getAllPriceListTemplateDetails(this.templateId)
      .subscribe({
        next: (response) => {
          this.templateDetails = response.body;
          this.formFields.forEach((field) => {
            const value = response.body[field.id as keyof PriceListTemplate];
            field.value =
              value ?? (field.type === "switch" ? false : null);
          });
        },
        error: (error) => {
          console.error("Error loading price list template detail:", error);
          this.notifier.notify("error", "Error loading Price List Template");
        },
      });
  }

  private addPriceListTemplate(): void {
    this.priceListTemplateService.addPriceListTemplate(this.buildModel()).subscribe({
      next: (response) => {
        if (response.code !== 0) {
          this.notifier.notify("error", response.message);
          return;
        }
        this.notifier.notify("success", "Price List Template added successfully");
        this.onCancel();
      },
      error: (error) => {
        console.error("Error adding price list template:", error);
        this.notifier.notify("error", "Error adding Price List Template");
      },
    });
  }

  private updatePriceListTemplate(): void {
    this.priceListTemplateService
      .updatePriceListTemplate({ ...this.buildModel(), ID: this.templateId })
      .subscribe({
        next: (response) => {
          if (response.code !== 0) {
            this.notifier.notify("error", response.message);
            return;
          }
          this.notifier.notify(
            "success",
            "Price List Template updated successfully",
          );
          this.onCancel();
        },
        error: (error) => {
          console.error("Error updating price list template:", error);
          this.notifier.notify("error", "Error updating Price List Template");
        },
      });
  }

  private buildModel(): Omit<PriceListTemplate, "ID"> {
    return {
      MODEL: this.getFieldValue("MODEL"),
      ROADTAX: this.toNullableNumber(this.templateDetails.ROADTAX),
      Price: this.toNullableNumber(this.templateDetails.Price),
      COE: this.toNullableNumber(this.templateDetails.COE),
      ENGINECAP: this.toNullableNumber(this.getFieldValue("ENGINECAP")),
      CATEGORY: String(this.getFieldValue("CATEGORY") ?? "").trim(),
      SEQ: this.toNullableNumber(this.getFieldValue("SEQ")),
      SUBSEQ: this.toNullableNumber(this.getFieldValue("SUBSEQ")),
      DisplayPassenger: Boolean(this.getFieldValue("DisplayPassenger")),
      DisplayCommercial: Boolean(this.getFieldValue("DisplayCommercial")),
      StringDesc: this.getFieldValue("StringDesc") ?? null,
    };
  }

  private getFieldValue(id: string): any {
    return this.formFields.find((field) => field.id === id)?.value;
  }

  private toNullableNumber(value: any): number | null {
    if (value === null || value === undefined || value === "") {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
}
